// src/Pages/Estabilishment/Support/index.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { postSupportMessage, SupportCategory } from "../../../Services/Support";
import { getMySubscription } from "../../../Services/Stripe";
import { IRestGetMySubscription } from "../../../Services/Interfaces";
import { whatsappLink, SUPPORT_WHATSAPP_LABEL } from "../../../Services/contacts";
import { useT } from "../../../i18n";

// ─── Plan helper (same logic as Banners) ─────────────────────────────────────
const resolvePlanType = (name?: string): string => {
  if (!name) return "insight";
  const lower = name.toLowerCase();
  if (lower.includes("showcase") || lower.includes("pulse") || lower.includes("wave")) return "showcase";
  if (lower.includes("connection") || lower.includes("meet"))                          return "connection";
  return "insight";
};

// ─── Icons ────────────────────────────────────────────────────────────────────
const ic = (p: React.ReactNode, s = 16) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{p}</svg>
);
const IconChevD     = () => ic(<><polyline points="6 9 12 15 18 9"/></>);
const IconWhatsApp  = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.71.45 3.38 1.3 4.85L2.05 22l5.36-1.4a9.9 9.9 0 0 0 4.63 1.18h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm0 18.13a8.2 8.2 0 0 1-4.18-1.14l-.3-.18-3.18.83.85-3.1-.2-.32a8.2 8.2 0 0 1-1.26-4.31c0-4.54 3.7-8.24 8.27-8.24a8.18 8.18 0 0 1 5.84 2.42 8.18 8.18 0 0 1 2.42 5.83c0 4.55-3.7 8.21-8.26 8.21zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.17.25-.64.81-.78.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.39-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.84-.86 2.05s.88 2.38 1 2.55c.12.17 1.74 2.66 4.22 3.72.59.25 1.05.4 1.41.52.59.19 1.13.16 1.55.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.46-.28z"/>
  </svg>
);

// ─── Categories ───────────────────────────────────────────────────────────────
const CATEGORIES: { value: SupportCategory; label: string }[] = [
  { value: "plans",          label: "Planos" },
  { value: "banners",        label: "Banners" },
  { value: "notifications",  label: "Notificações" },
  { value: "establishments", label: "Estabelecimentos" },
  { value: "account",        label: "Conta" },
  { value: "others",         label: "Outros" },
];

// ─── Styles ───────────────────────────────────────────────────────────────────
const inp = (extra?: React.CSSProperties): React.CSSProperties => ({
  width: "100%", padding: "9px 12px", border: "1.5px solid #e8e8e8",
  borderRadius: 8, fontSize: 13, color: "#333", outline: "none",
  background: "#fff", boxSizing: "border-box", ...extra,
});
const lbl: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 4, display: "block" };
const req = <span style={{ color: "#ef4444" }}> *</span>;

// ─── WhatsApp ─────────────────────────────────────────────────────────────────
// Número e formatação vêm de Services/contacts.ts — ponto único dos canais.
const WHATSAPP_MESSAGE = "Olá! Preciso de ajuda com a plataforma Pertalks.";

// ─── Main ─────────────────────────────────────────────────────────────────────
export const Support = () => {
  const t = useT();
  const navigate = useNavigate();
  const whatsappUrl = whatsappLink(t(WHATSAPP_MESSAGE));

  const [category, setCategory] = useState<SupportCategory | "">("");
  const [subject,  setSubject]  = useState("");
  const [message,  setMessage]  = useState("");
  const [sending,  setSending]  = useState(false);

  // WhatsApp só visível para plano Connection (meet) ou Showcase
  const [whatsappAllowed, setWhatsappAllowed] = useState(false);

  useEffect(() => {
    getMySubscription()
      .then((res: any) => {
        const sub  = (res as IRestGetMySubscription).data.data;
        const type = resolvePlanType(sub?.plan?.name);
        setWhatsappAllowed(type === "connection" || type === "showcase");
      })
      .catch(() => setWhatsappAllowed(false));
  }, []);

  const isValid = category !== "" && subject.trim() !== "" && message.trim() !== "";

  const handleSubmit = () => {
    if (!isValid) return;
    setSending(true);
    postSupportMessage({ category: category as SupportCategory, subject: subject.trim(), message: message.trim() })
      .then((res: any) => {
        if (res) {
          setCategory("");
          setSubject("");
          setMessage("");
        }
      })
      .catch(() => {})
      .finally(() => setSending(false));
  };

  return (
    <div style={{ position: "relative", minHeight: "calc(100vh - 120px)" }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#111" }}>{t("Suporte")}</h2>

      <div style={{ background: "#fff", border: "1px solid #f0f0f0", borderRadius: 14, padding: "28px 32px", marginTop: 20 }}>

        <p style={{ margin: "0 0 16px", fontSize: 13, color: "#555" }}>
          {t("Para sugestões, dúvidas ou outras informações, entre em contato conosco pelo formulário abaixo.")}
        </p>

        {/* Canal direto — exibe o número para o usuário poder salvar o contato,
            já que o botão flutuante só abre a conversa. */}
        {whatsappAllowed && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 10,
              padding: "12px 14px",
              marginBottom: 20,
            }}
          >
            <span style={{ color: "#25D366", flexShrink: 0 }}>
              <IconWhatsApp />
            </span>
            <p style={{ margin: 0, fontSize: 13, color: "#166534", lineHeight: 1.6 }}>
              {t("Atendimento prioritário no WhatsApp:")}{" "}
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#166534", fontWeight: 700, textDecoration: "underline" }}
              >
                {SUPPORT_WHATSAPP_LABEL}
              </a>
            </p>
          </div>
        )}

        {/* Categoria + Assunto */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
          <div>
            <label style={lbl}>{t("Categoria")}{req}</label>
            <div style={{ position: "relative" }}>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as SupportCategory)}
                style={{ ...inp(), appearance: "none", paddingRight: 32, cursor: "pointer", color: category ? "#333" : "#aaa" }}
              >
                <option value="" disabled>{t("Selecione a categoria")}</option>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value} style={{ color: "#333" }}>{t(c.label)}</option>
                ))}
              </select>
              <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#00b6bc", pointerEvents: "none" }}>
                <IconChevD />
              </span>
            </div>
          </div>

          <div>
            <label style={lbl}>{t("Assunto")}{req}</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t("Informe o assunto")}
              style={inp()}
            />
          </div>
        </div>

        {/* Mensagem */}
        <div style={{ marginBottom: 24 }}>
          <label style={lbl}>{t("Mensagem")}{req}</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("Digite a mensagem do push")}
            rows={6}
            style={{ ...inp(), resize: "vertical", minHeight: 140, fontFamily: "inherit", lineHeight: 1.5 }}
          />
        </div>

        {/* Actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={() => navigate(-1)}
            style={{ padding: "10px 28px", background: "#fff", border: "1.5px solid #00b6bc", borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: "pointer", color: "#00b6bc" }}
          >
            {t("Voltar")}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid || sending}
            style={{ padding: "10px 28px", background: "#00b6bc", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: (!isValid || sending) ? "not-allowed" : "pointer", color: "#fff", display: "flex", alignItems: "center", gap: 7, opacity: (!isValid || sending) ? 0.6 : 1 }}
          >
            {sending && <CircularProgress size={14} color="inherit" />} {t("Enviar mensagem")}
          </button>
        </div>
      </div>

      {/* WhatsApp floating button — apenas plano Connection e Showcase */}
      {whatsappAllowed && (
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            position: "fixed", bottom: 24, right: 24, zIndex: 50,
            display: "flex", alignItems: "center", gap: 8,
            background: "#25D366", color: "#fff", textDecoration: "none",
            padding: "12px 20px", borderRadius: 999, fontWeight: 700, fontSize: 14,
            boxShadow: "0 4px 14px rgba(37,211,102,0.4)",
          }}
        >
          <IconWhatsApp /> WhatsApp
        </a>
      )}
    </div>
  );
};